import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Search, Building2, Users, Briefcase, TrendingUp, DollarSign,
  FileText, BarChart3, Shield, ChevronRight, X, Layers, BookOpen,
  PanelLeftClose, PanelLeftOpen, Activity, Mail, AlertTriangle, Database, Inbox, Eye, Brain
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_SECTIONS = [
  { id: "overview", label: "Dashboard", icon: Activity },
  { id: "divider-deals", divider: true, label: "DEAL MANAGEMENT" },
  { id: "command_center", label: "Deal Center", icon: Building2 },
  { id: "marketplace_preview", label: "Marketplace", icon: Eye },
  { id: "divider-crm", divider: true, label: "CRM" },
  { id: "crm_dashboard", label: "Dashboard", icon: BarChart3 },
  { id: "borrowers", label: "Borrowers", icon: Users },
  { id: "lenders", label: "Lenders", icon: Briefcase },
  { id: "investors", label: "Investors", icon: TrendingUp },
  { id: "email_outreach", label: "Email & Outreach", icon: Mail },
  { id: "divider-capital", divider: true, label: "CAPITAL" },
  { id: "capital", label: "Capital Engine", icon: DollarSign },
  { id: "servicing", label: "Servicing", icon: Activity },
  { id: "funds", label: "Fund I", icon: Layers },
  { id: "opportunities", label: "Opportunities", icon: TrendingUp },
  { id: "revenue", label: "Revenue", icon: BarChart3 },
  { id: "divider-docs", divider: true, label: "RECORDS" },
  { id: "doc_governance", label: "Documents", icon: FileText },
  { id: "report_center", label: "Reports", icon: BookOpen },
  { id: "divider-intelligence", divider: true, label: "INTELLIGENCE" },
  { id: "copilot", label: "AI Copilot", icon: Brain },
  { id: "ai_agents", label: "AI Agents", icon: Activity },
  { id: "engagement_scoring", label: "Engagement", icon: BarChart3 },
  { id: "conversion", label: "Conversion", icon: TrendingUp },
  { id: "divider-system", divider: true, label: "SYSTEM" },
  { id: "acquisitions", label: "Acquisitions", icon: Building2 },
  { id: "activity_feed", label: "Activity Feed", icon: Activity },
  { id: "approvals", label: "Approvals", icon: Shield },
  { id: "maker_checker", label: "Maker-Checker", icon: Shield },
  { id: "tax_exports", label: "Tax Exports", icon: FileText },
  { id: "investor_digests", label: "Investor Digests", icon: Mail },
  { id: "exception_queue", label: "Exceptions", icon: AlertTriangle },
  { id: "integrity", label: "Integrity", icon: Shield },
  { id: "platform_health", label: "Platform Health", icon: Activity },
  { id: "team", label: "Team", icon: Users },
  { id: "migration", label: "Migration", icon: Database },
  { id: "security", label: "Security", icon: Shield },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

const ENTITY_ICONS = { deal: Building2, borrower: Users, lender: Briefcase, investor: TrendingUp, opportunity: DollarSign };
const ENTITY_COLORS = {
  deal: "text-emerald-600 bg-emerald-50",
  borrower: "text-blue-600 bg-blue-50",
  lender: "text-purple-600 bg-purple-50",
  investor: "text-amber-600 bg-amber-50",
  opportunity: "text-cyan-600 bg-cyan-50",
};

export default function AdminNavSidebar({ activeSection, onSectionChange, onEntityNavigate, pendingCount = 0 }) {
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/entity/search?q=${encodeURIComponent(q)}`, { headers });
      setSearchResults(res.data.results || []);
    } catch (err) { console.error(err); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(searchQuery), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery, doSearch]);

  // Close search on click outside
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Primary sections for mobile tab bar (subset of most important items)
  const MOBILE_NAV = NAV_SECTIONS.filter(s => !s.divider);

  return (
    <>
      {/* Mobile: horizontal scrollable tabs */}
      <div className="md:hidden -mx-4 px-4 mb-4 overflow-x-auto scrollbar-hide" data-testid="admin-nav-mobile">
        <div className="flex gap-1.5 pb-2" style={{ minWidth: "max-content" }}>
          {MOBILE_NAV.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            const showBadge = item.id === "approvals" && pendingCount > 0;
            return (
              <button key={item.id} onClick={() => onSectionChange(item.id)}
                data-testid={`admin-nav-mobile-${item.id}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  isActive ? "bg-emerald-700 text-white" : "bg-white text-[#666] border border-[#e8e0d0]"
                }`}>
                <Icon className={`h-3.5 w-3.5 ${isActive ? "text-current" : "text-[#999]"}`} />
                {item.label}
                {showBadge && <span className="ml-1 px-1 py-0.5 rounded-full text-[8px] font-bold bg-amber-500 text-white">{pendingCount}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop: vertical sidebar */}
      <div className={`hidden md:flex shrink-0 bg-white border-r border-[#e8e0d0] flex-col transition-all duration-300 ${collapsed ? "w-14" : "w-56"}`}
        data-testid="admin-nav-sidebar">
        {/* Collapse toggle */}
        <div className="flex items-center justify-between px-3 py-3 border-b border-[#e8e0d0]">
          {!collapsed && <span className="text-[10px] font-bold uppercase tracking-widest text-[#999]">Navigation</span>}
          <button onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded hover:bg-[#faf7f2] text-[#888] transition-colors"
            data-testid="sidebar-collapse-btn">
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>

        {/* Search */}
        {!collapsed && (
          <div className="px-3 py-2 border-b border-[#e8e0d0] relative" ref={searchRef}>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#bbb]" />
              <input type="text" value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                placeholder="Search entities..."
                className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-[#e8e0d0] text-xs focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none bg-[#faf7f2]"
                data-testid="sidebar-search-input" />
            </div>

            {/* Search Dropdown */}
            <AnimatePresence>
              {searchOpen && searchQuery.length >= 2 && (
                <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                  className="absolute left-3 right-3 top-full mt-1 bg-white rounded-xl border border-[#e8e0d0] shadow-xl z-50 max-h-64 overflow-y-auto"
                  data-testid="sidebar-search-results">
                  {searching ? (
                    <div className="py-4 text-center text-xs text-[#999]">Searching...</div>
                  ) : searchResults.length === 0 ? (
                    <div className="py-4 text-center text-xs text-[#999]">No results for "{searchQuery}"</div>
                  ) : (
                    searchResults.map((r, i) => {
                      const Icon = ENTITY_ICONS[r.type] || Building2;
                      const colors = ENTITY_COLORS[r.type] || "text-gray-600 bg-gray-50";
                      return (
                        <button key={`${r.type}-${r.id}-${i}`}
                          onClick={() => { onEntityNavigate(r.type, r.id); setSearchQuery(""); setSearchOpen(false); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[#faf7f2] transition-colors text-left border-b border-[#f0ebe0] last:border-0"
                          data-testid={`search-result-${r.type}-${i}`}>
                          <div className={`p-1.5 rounded ${colors}`}><Icon className="h-3 w-3" /></div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-[#333] truncate">{r.label}</p>
                            <p className="text-[9px] text-[#999] truncate">{r.sublabel}</p>
                          </div>
                          <ChevronRight className="h-3 w-3 text-[#ddd] shrink-0" />
                        </button>
                      );
                    })
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Nav Items */}
        <nav className="flex-1 overflow-y-auto py-1">
          {NAV_SECTIONS.map((item) => {
            if (item.divider) {
              if (collapsed) return <div key={item.id} className="h-px bg-[#e8e0d0] mx-2 my-1.5" />;
              return (
                <div key={item.id} className="px-3 pt-4 pb-1">
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[#bbb]">{item.label}</span>
                </div>
              );
            }

            const Icon = item.icon;
            const isActive = activeSection === item.id;
            const showBadge = item.id === "approvals" && pendingCount > 0;

            return (
              <button key={item.id} onClick={() => onSectionChange(item.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700 border-r-2 border-emerald-600"
                    : "text-[#666] hover:bg-[#faf7f2] hover:text-[#333]"
                } ${collapsed ? "justify-center" : ""}`}
                title={collapsed ? item.label : undefined}
                data-testid={`sidebar-nav-${item.id}`}>
                <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-emerald-600" : "text-[#999]"}`} />
                {!collapsed && (
                  <>
                    <span className="text-xs font-medium flex-1">{item.label}</span>
                    {showBadge && (
                      <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-500 text-white">{pendingCount}</span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </>
  );
}
